import { FcCalendar, FcRight } from 'react-icons/fc';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { useArchiveWarehouseDeliveryMutation, WarehouseDeliveryFragmentFragment } from 'data/__generated__';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

import { formatDateAsAnniversary } from '../../../helpers/dateTimes';
import { Loader, LoaderModeType } from 'components/Loader';

export function ArchivePopup({
    warehouseDeliveries,
    selectedWarehouseDeliveryIds,
    setSelectedWarehouseDeliveryIds,
    isOpen,
    setIsOpen,
}: {
    warehouseDeliveries: WarehouseDeliveryFragmentFragment[];
    selectedWarehouseDeliveryIds: string[];
    setSelectedWarehouseDeliveryIds: (warehouseDeliveryIds: string[]) => void;
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
}) {
    const selectedWarehouseDeliveries = warehouseDeliveries.filter((warehouseDelivery) =>
        selectedWarehouseDeliveryIds.includes(warehouseDelivery._id),
    );

    const [archiveWarehouseDelivery, { loading }] = useArchiveWarehouseDeliveryMutation();

    async function handleSubmit() {
        let successCount = 0;
        let errorCount = 0;

        for (const warehouseDeliveryId of selectedWarehouseDeliveryIds) {
            try {
                const { data } = await archiveWarehouseDelivery({
                    variables: { warehouseDeliveryId },
                });

                if (data?.archiveWarehouseDelivery?.success) {
                    successCount++;
                } else {
                    errorCount++;
                    const errorMessage = data?.archiveWarehouseDelivery?.error || 'Erreur inconnue';
                    toast.error(`Erreur lors de l'archivage de la livraison ${warehouseDeliveryId}: ${errorMessage}`);
                }
            } catch (error) {
                errorCount++;
                toast.error(`Erreur lors de l'archivage de la livraison ${warehouseDeliveryId}: ${error}`);
            }
        }

        if (successCount > 0) {
            toast.success(`Livraison(s) d'entrepôt archivée(s) : ${successCount}`);
        }

        if (errorCount === 0) {
            setSelectedWarehouseDeliveryIds([]);
            setIsOpen(false);
        }
    }

    return (
        <TotemPopup title="Êtes-vous sûr.e ?" isOpen={isOpen} setIsOpen={setIsOpen}>
            Vous vous apprêtez à archiver {selectedWarehouseDeliveries.length} livraison(s) d'entrepôt. Souhaitez-vous
            vraiment archiver la/les livraison(s) d'entrepôt suivante(s) ?
            <WarehouseDeliveryList>
                {selectedWarehouseDeliveries.map((warehouseDelivery) => {
                    const { _id: warehouseDeliveryId, deliveryDate, site } = warehouseDelivery;

                    return (
                        <WarehouseDelivery key={warehouseDeliveryId}>
                            <WarehouseDeliveryDate>
                                <FcCalendar size={26} />
                                {deliveryDate
                                    ? formatDateAsAnniversary({
                                          dateTime: deliveryDate,
                                          displayDay: true,
                                      })
                                    : 'Date non définie'}
                            </WarehouseDeliveryDate>
                            <WarehouseDeliverySite>
                                <FcRight size={26} />
                                {site?.name || warehouseDeliveryId}
                            </WarehouseDeliverySite>
                        </WarehouseDelivery>
                    );
                })}
            </WarehouseDeliveryList>
            <SubmitContainer>
                <TotemPrimaryButton onClick={handleSubmit}>
                    {loading ? <Loader size="20px" mode={LoaderModeType.Spin} /> : 'Confirmer'}
                </TotemPrimaryButton>
            </SubmitContainer>
        </TotemPopup>
    );
}

const WarehouseDeliveryList = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    padding-left: 50px;
    overflow: auto;

    & > :not(:first-child) {
        margin-top: 5px;
    }
`;

const WarehouseDelivery = styled.span`
    display: inline-flex;
    align-items: center;
`;

const WarehouseDeliveryDate = styled.span`
    display: inline-flex;
    align-items: center;
`;

const WarehouseDeliverySite = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 20px;
    font-weight: 800;
`;

const SubmitContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
`;
